// src/data/straitScenes.js
// =============================================================
// v15 — 八海咽喉 (eight-straits-2026)
//
// 本次異動 (v15):
//  * 桌機/手機分版 bbox: bbox → bboxDesktop + bboxMobile
//    每個 scene 都有兩組座標,StraitMap.svelte 用 $isMobile 切換
//    桌機 viewport 假設 1200×800 (aspect 1.50)
//    手機 viewport 假設 390×750  (aspect 0.52)
//  * 11 個 scene 全部依 Google Maps 截圖實機校準
//  * 取景策略多為「桌機廣域脈絡 + 手機聚焦特寫」,
//    少數例外 (taiwan_strait 兩者都偏特寫,以區分 combined)
//
// 沿用 (v13/v14):
//  * MAP_PRESETS — 11 個視覺規範 preset (countryLabel / seaLabel /
//    chokepointPrimary / chokepointSecondary / port / emphasisPoint /
//    routeActual / routeAlternate / pipelineGas / strategicLine /
//    threatZone)
//  * layer 新格式: { id, preset, data }
//  * 三層樣式覆蓋:preset → layer.styleOverride → feature.properties
//  * visible_in 共用 GeoJSON,sceneId 過濾
//  * alwaysVisible 是 scene 層級決定 (不是 preset)
//  * 合併 scene (mandeb_hormuz_combined / taiwan_strait_combined /
//    turkish_straits_combined; magellan_scene_combined 已於 v14 刪除)
// =============================================================

const LAND_110M = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'
const LAND_50M  = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json'

// =============================================================
// MAP_PRESETS — 地圖標註的視覺設計系統
//
// 11 個 preset 涵蓋所有 scene 會用到的視覺類型。
// 每個 layer 透過 preset: 'xxx' 引用，整份規範改一處全部 scene 同步。
//
// 三層樣式覆蓋規則 (在 StraitMap.svelte 的 drawLayer 內實作):
//   1. preset 預設值           ← MAP_PRESETS[layer.preset]
//   2. layer 層級 override     ← layer.styleOverride (整層共用，少數情況)
//   3. feature 層級 override   ← feature.properties (單一 feature 特例)
//
// 例如:
//   chokepointPrimary preset 預設 labelOffsetX: 20 (標注在右)
//   博斯普魯斯海峽 feature 加 properties.labelOffsetX: -20 → 標注改在左
// =============================================================
export const MAP_PRESETS = {
  // -- 點類 (6) ----------------------------------------------

  // 1. 國家名稱 — 純文字標注，最常用
  countryLabel: {
    type: 'point',
    showDot: false,
    fontSize: 16,
    fontSizeMobile: 14,
    fontWeight: 500,
    fontColor: '#25262A',
    labelAnchor: 'middle',
    labelOffsetX: 0,
    labelOffsetY: 0,
  },

  // 2. 海域名稱 — 比國家小、中性灰
  seaLabel: {
    type: 'point',
    showDot: false,
    fontSize: 14,
    fontSizeMobile: 12,
    fontWeight: 400,
    fontColor: '#7F8490',
    labelAnchor: 'middle',
    labelOffsetX: 0,
    labelOffsetY: 0,
  },

  // 3. 主角海峽 — 紅空心圈 + 右側粗體紅字
  chokepointPrimary: {
    type: 'point',
    shape: 'circle',
    fill: 'transparent',
    stroke: '#d44545',
    strokeWidth: 2,
    radius: 14,
    showLabel: true,
    labelAnchor: 'start',
    labelOffsetX: 20,
    labelOffsetY: 4,
    fontSize: 18,
    fontSizeMobile: 14,
    fontColor: '#d44545',
    fontWeight: 700,
    textStroke: '#f3f1ec',
    textStrokeWidth: 4,
  },

  // 4. 次要海峽 — 純文字參考，不畫圈
  chokepointSecondary: {
    type: 'point',
    showDot: false,
    fontSize: 14,
    fontSizeMobile: 12,
    fontWeight: 500,
    fontColor: '#E46962',
    labelAnchor: 'middle',
    labelOffsetX: 0,
    labelOffsetY: 0,
  },

  // 5. 港口 — 海藍實心圓 + 右側標注
  //   [v13] 加入 textStroke 配置:讓港口名能在管線等線條上保持可讀
  port: {
    type: 'point',
    shape: 'circle',
    fill: '#005F83',
    stroke: 'none',
    radius: 5,
    showLabel: true,
    labelAnchor: 'start',
    labelOffsetX: 10,
    labelOffsetY: -8,
    fontSize: 14,
    fontSizeMobile: 12,
    fontColor: '#005F83',
    fontWeight: 600,
    textStroke: '#f3f1ec',
    textStrokeWidth: 4,
  },

  // 6. 強調點 — 琥珀菱形 + 粗體標注 (城市/島嶼/腳本標明「特別標注」)
  emphasisPoint: {
    type: 'point',
    shape: 'diamond',
    fill: '#c69020',
    stroke: 'none',
    radius: 6,
    showLabel: true,
    labelAnchor: 'start',
    labelOffsetX: 10,
    labelOffsetY: 4,
    fontSize: 14,
    fontSizeMobile: 12,
    fontColor: '#8a5f10',
    fontWeight: 700,
  },

  // -- 線類 (4) ----------------------------------------------

  // 7. 現行航線 — 藍實線
  routeActual: {
    type: 'line',
    stroke: '#3e6a8a',
    strokeWidth: 3,
    showLabel: true,
    fontSize: 14,
    fontSizeMobile: 12,
    fontColor: '#3e6a8a',
    fontWeight: 500,
  },

  // 8. 替代/假設航線 — 藍虛線 (同色不同樣式)
  routeAlternate: {
    type: 'line',
    stroke: '#3e6a8a',
    strokeWidth: 3,
    strokeDasharray: '6 4',
    showLabel: true,
    fontSize: 14,
    fontSizeMobile: 12,
    fontColor: '#3e6a8a',
    fontWeight: 500,
  },

  // 9. 油氣管線 — 琥珀實線 (區別於船舶航線)
  pipelineGas: {
    type: 'line',
    stroke: '#D0810D',
    strokeWidth: 3,
    showLabel: true,
    fontSize: 14,
    fontSizeMobile: 12,
    fontColor: '#D0810D',
    fontWeight: 500,
  },

  // 10. 戰略邊界 — 灰虛線 (第一島鏈等)
  strategicLine: {
    type: 'line',
    stroke: '#8a8a8a',
    strokeWidth: 4,
    strokeDasharray: '8 6',
    showLabel: true,
    fontSize: 18,
    fontSizeMobile: 16,
    fontColor: '#8a8a8a',
    fontWeight: 400,
  },

  // -- 面類 (1) ----------------------------------------------

  // 11. 威脅範圍 — 紅半透明
  threatZone: {
    type: 'polygon',
    fill: '#d44545',
    fillOpacity: 0.12,
    stroke: '#d44545',
    strokeWidth: 1.5,
    strokeDasharray: '4 3',
  },
}

// =============================================================
// 地圖結構定義
// =============================================================
export const straitScenes = {
  // ============================================================
  // 1. 荷姆茲海峽
  //   [v13] 已遷移到 preset + URL 載入 (middle_east region)
  //         GeoJSON 共用,以 visible_in 屬性過濾
  // ============================================================
  hormuz_strait: {
    id: 'hormuz_strait',
    navLabel: '荷姆茲海峽',
    title: '荷姆茲海峽 Strait of Hormuz',
    // [v15] 桌機/手機分版 bbox。桌機:整個能源運輸命脈視角
    //   (波斯灣→阿曼灣→巴基斯坦西部);手機:直式特寫,從伊朗德黑蘭
    //   到阿曼南部,海峽紅圈在畫面正中。perfect fit (桌 5%/手 2% padding)
    bboxDesktop: [[45, 19], [67, 31]],
    bboxMobile:  [[51, 19], [60, 35]],
    landSource: LAND_50M,
    layers: [
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/middle_east/countries.geojson' },
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/middle_east/seas.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/middle_east/chokepoints_primary.geojson' },
    ],
  },

  // ============================================================
  // 合併 scene - 荷姆茲 ↔ 曼德 (含替代管線)
  // 由 ArchieML 的 strait_id_override: mandeb_hormuz_combined 切入
  //   [v13] 已遷移到 preset + URL 載入 (middle_east region)
  //         GeoJSON 與 hormuz_strait / bab_el_mandeb 共用,以 visible_in 過濾
  //         port/chokepoint 為地圖背景 (alwaysVisible),管線為敘事 toggle
  // ============================================================
  mandeb_hormuz_combined: {
    id: 'mandeb_hormuz_combined',
    navLabel: '荷姆茲↔曼德',
    title: '荷姆茲海峽 ↔ 曼德海峽 替代路徑',
    // [v15] 桌機收窄,東邊削掉印度 (敘事不需);手機保留北延 (整個阿拉伯
    //   半島入鏡)。桌機 11% 左右 padding,手機 17% 上下 padding
    bboxDesktop: [[34, 10], [62, 32]],
    bboxMobile:  [[37, 12], [64, 42]],
    landSource: LAND_50M,
    // [v13] layers 繪製順序由下到上:面 → 線 → 點,重要性遞增
    //       管線在港口之前畫,讓港口蓋過管線交會處
    layers: [
      // 1) 海域文字 (背景文字,最底層)
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/middle_east/seas.geojson' },

      // 2) 線:管線 (step toggle 元素,敘事 reveal 用)
      // featurePropertyOverrides:GeoJSON 是英文 name,套上中文標註與位置
      {
        id: 'pipelines_gas', preset: 'pipelineGas',
        legendLabel: '替代輸油管道',
        data: '/geo/middle_east/pipelines_gas.geojson',
        featurePropertyOverrides: {
          'Bab-Habshan–Fujairah Oil Pipeline': {
            name: '阿布達比輸油管道',
            labelLng: 55.0,
            labelLat: 24.0,
          },
          'East-West Crude Oil Pipeline': {
            name: '東西向輸油管道',
            labelLng: 44.5,
            labelLat: 25.5,
          },
        },
      },

      // 3) 國家文字
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/middle_east/countries.geojson' },

      // 4) 點:港口 (蓋過管線交會處)
      { id: 'ports', preset: 'port', alwaysVisible: true,
        data: '/geo/middle_east/ports.geojson' },

      // 5) 點:海峽紅圈 (最重要的視覺,在最上層)
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/middle_east/chokepoints_primary.geojson',
        featurePropertyOverrides: {
          '荷姆茲海峽': {
            labelLng: 55.0,   // 從紅圈 56.25 往左偏 1.25°
            labelLat: 27.5,   // 從紅圈 26.5 往上偏 1°
          },
        },
      },
    ],
  },

  // ============================================================
  // 2. 曼德海峽
  //   [v13] 全部遷移到 URL (middle_east region)
  //         (取消原本的 shipping_lanes / attack_zone,只保留地圖背景)
  // ============================================================
  bab_el_mandeb: {
    id: 'bab_el_mandeb',
    navLabel: '曼德海峽',
    title: '曼德海峽 Bab-el-Mandeb',
    // [v15] 桌機縱向擴大到索馬利亞中部 (perfect fit + 海盜威脅脈絡);
    //   手機直式從沙烏地南部到衣索比亞。桌 0%/手 16% padding
    bboxDesktop: [[38, 6], [55, 17]],
    bboxMobile:  [[36, 7], [53, 28]],
    landSource: LAND_50M,
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/middle_east/seas.geojson' },
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/middle_east/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/middle_east/chokepoints_primary.geojson' },
    ],
  },

  // ============================================================
  // 3. 馬六甲海峽
  //   [v13] 已遷移到 preset + URL 載入 (southeast_asia region)
  //         bbox 維持窄視野(馬六甲特寫),原 shipping_lanes / ports
  //         layer 拿掉 (QGIS 未 export)
  // ============================================================
  malacca_strait: {
    id: 'malacca_strait',
    navLabel: '馬六甲海峽',
    title: '馬六甲海峽 Malacca Strait',
    // [v15] 桌機:整個東南亞海事十字路口 (含婆羅洲、蘇拉威西、爪哇,
    //   紅圈偏左但強調樞紐脈絡);手機:直式從泰國灣到爪哇北岸。桌 8%/手 9% padding
    bboxDesktop: [[95, -7], [121, 7.5]],
    bboxMobile:  [[96, -6.5], [109, 14]],
    landSource: LAND_50M,
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/southeast_asia/seas.geojson' },
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/southeast_asia/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/southeast_asia/chokepoints_primary.geojson',
        featurePropertyOverrides: {
        '馬六甲海峽': {
          labelLng: 99.5,   // 紅圈往左 2°,進海峽中央
          labelLat: 5.5,
        },
      }, },
    ],
  },

  // ============================================================
  // 4. 麥哲倫海峽
  //   [v13] 已遷移到 preset + URL 載入 (americas region)
  //         里奧格蘭德是腳本明確要求「特別標注」的地點,
  //         透過 emphasis_points (菱形 + 琥珀色) 強調
  //         旁達阿里納用 emphasisPoint 而非 port (場景沒港口圖層)
  //         原 cities / magellan_route layer 拿掉 (QGIS 未 export)
  //   [v13] 刪除 magellan_scene_combined scene (與 ArchieML 同步)
  // ============================================================
  magellan: {
    id: 'magellan',
    navLabel: '麥哲倫海峽',
    title: '麥哲倫海峽 Strait of Magellan',
    // [v15] 桌機:南大西洋全景,含福克蘭群島、南喬治亞、南極半島北端
    //   (敘事延伸:福克蘭戰爭、南極通道);手機:傳統麥哲倫海峽特寫,
    //   v14 微擴。桌 3%/手 18% padding (磯外大量純海洋可接受)
    bboxDesktop: [[-76, -56], [-64, -50]],
    bboxMobile:  [[-76, -56], [-63, -46]],
    landSource: LAND_50M,
    layers: [
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/americas/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/americas/chokepoints_primary.geojson' },
      { id: 'emphasis_points', preset: 'emphasisPoint', alwaysVisible: true,
        data: '/geo/americas/emphasis_points.geojson' },
    ],
  },

  // ============================================================
  // 5. 台灣海峽
  // ============================================================
  taiwan_strait: {
    id: 'taiwan_strait',
    navLabel: '台灣海峽',
    title: '台灣海峽 Taiwan Strait',
    // [v15] 桌機:特寫,台海+菲律賓+琉球 (combined 才拉到日本);
    //   手機:直式僅看台海+浙閩沿海。桌 14% 左右/手 14% 上下 padding
    bboxDesktop: [[112, 17], [142, 38]],
    bboxMobile:  [[114, 13], [133, 39]],
    landSource: LAND_50M,
    // [v13] 已遷移到 preset + URL 載入 (east_asia region)
    //   GeoJSON 與 taiwan_strait_combined / miyako_strait 共用,以 visible_in 過濾
    //   原 adiz / median_line / shipping_lanes layer 拿掉 (QGIS 未 export)
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/east_asia/seas.geojson' },
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/east_asia/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/east_asia/chokepoints_primary.geojson',
        featurePropertyOverrides: {
          '台灣海峽': {
            labelLng: 117.5,   // 紅圈往左 1.5°
            labelLat: 23,      // 紅圈往下 1°
          },
        }, 
      },
    ],
  },

  // ============================================================
  // 合併 scene - 日本貿易航線必經台灣海峽
  // 由 ArchieML 的 strait_id_override: taiwan_strait_combined 切入
  //   [v13] 已遷移到 preset + URL 載入 (east_asia region)
  //         bbox 收窄為東亞 (115E-142E),日本貿易航線從畫面外延伸進來,
  //         視覺像「來自遠方的箭」契合敘事
  // ============================================================
  taiwan_strait_combined: {
    id: 'taiwan_strait_combined',
    navLabel: '日本貿易航線',
    title: '日本貿易航線必經台灣海峽',
    // [v15] 桌機:從台海延伸到日本中部 (航線終點),v14 微擴;
    //   手機:直式從馬尼拉到平壤,完整航線。桌 8%/手 6% padding
    bboxDesktop: [[112, 17], [142, 38]],
    bboxMobile:  [[115, 15], [132, 40]],
    landSource: LAND_50M,
    layers: [
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/east_asia/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/east_asia/chokepoints_primary.geojson' },

      // 日本貿易航線 — step toggle 元素
      // [v13] GeoJSON 沒 labelLng/labelLat,featurePropertyOverrides 補
      {
        id: 'routes_actual', preset: 'routeActual',
        legendLabel: '日本貿易航線',
        data: '/geo/east_asia/routes_actual.geojson',
        featurePropertyOverrides: {
          '日本貿易航線': {
            labelLng: 125.0,
            labelLat: 28.5,
          },
        },
      },
    ],
  },

  // ============================================================
  // 6. 宮古海峽
  // ============================================================
  // ============================================================
  // 6. 宮古海峽
  //   [v13] 已遷移到 preset + URL 載入 (east_asia region)
  //         原 china_route / bases layer 拿掉 (QGIS 未 export);
  //         emphasis_points 改用菱形 preset 標出三個島嶼
  // ============================================================
  miyako_strait: {
    id: 'miyako_strait',
    navLabel: '宮古海峽',
    title: '宮古海峽 Miyako Strait',
    // [v15] 桌機:v14 保持不動 (本來就 perfect fit, 左右 1% padding);
    //   手機:直式延展到上海/寧波,第一島鏈完整入鏡。手 19% padding
    bboxDesktop: [[122, 22], [130, 27]],
    bboxMobile:  [[121, 22], [129.5, 31]],
    landSource: LAND_50M,
    layers: [
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/east_asia/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/east_asia/chokepoints_primary.geojson' },

      // 第一島鏈 — alwaysVisible (這個 scene 的核心敘事元素之一,
      //   跟宮古海峽紅圈、三個島嶼一樣是「地圖背景的主角」,不該等
      //   step toggle 才顯示;移除 legendLabel 因為不再進 legend)
      // [v13.2] 從 step toggle 改成 alwaysVisible
      // [v13.3] 第一島鏈是敘事重點:fontSize 12 → 14,labelLat 上移貼近線段
      // [v13.4] 加 fontSizeMobile 給 RWD
      {
        id: 'strategic_lines', preset: 'strategicLine', alwaysVisible: true,
        data: '/geo/east_asia/strategic_lines.geojson',
        featurePropertyOverrides: {
          '第一島鏈': {
            labelLng: 125.8,
            labelLat: 23.5,
            fontSize: 18,
            fontSizeMobile: 16,
            fontWeight: 500,
          },
        },
      },

      // 強調點:島嶼 (alwaysVisible 因為是地圖背景)
      { id: 'emphasis_points', preset: 'emphasisPoint', alwaysVisible: true,
        data: '/geo/east_asia/emphasis_points.geojson' },
    ],
  },

  // ============================================================
  // 7. 直布羅陀海峽
  // ============================================================
  gibraltar: {
    id: 'gibraltar',
    navLabel: '直布羅陀海峽',
    title: '直布羅陀海峽 Strait of Gibraltar',
    // [v15] 桌機:廣域含馬德拉群島、薩丁尼亞、撒哈拉北緣 (整個西地中海
    //   入口);手機:伊比利半島南半+摩洛哥北。桌 7%/手 10% padding
    bboxDesktop: [[-17, 30], [9, 42]],
    bboxMobile:  [[-9, 31], [-1, 41]],
    landSource: LAND_50M,
    // [v13] 已遷移到 preset + URL 載入 (europe region)
    //       GeoJSON 與 turkish_straits / turkish_straits_combined 共用,
    //       以 visible_in 過濾
    //       原 shipping_lanes / territories layer 拿掉 (QGIS 未 export)
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/europe/seas.geojson' },
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/europe/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/europe/chokepoints_primary.geojson' },
    ],
  },

  // ============================================================
  // 8. 黑海海峽 (土耳其海峽)
  // ============================================================
  turkish_straits: {
    id: 'turkish_straits',
    navLabel: '黑海海峽',
    title: '黑海海峽 Turkish Straits',
    // [v15] 桌機:廣域,從亞得里亞海到高加索西緣 (黑海+巴爾幹+安納托利亞
    //   完整地緣);手機:依截圖,黑海西岸→地中海北端 (捨棄黑海東岸)。
    //   桌 14%/手 3% padding
    bboxDesktop: [[14, 35], [44, 46]],
    bboxMobile:  [[25, 36.5], [32, 46]],
    landSource: LAND_50M,
    // [v13] 已遷移到 preset + URL 載入 (europe region)
    //       兩個 chokepoint (博斯普魯斯 + 達達尼爾) 都顯示
    //       原 bosphorus / dardanelles / cities layer 拿掉 (QGIS 未 export
    //       對應的詳細幾何;紅圈足以代表海峽位置)
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/europe/seas.geojson' },
      { id: 'country_names', preset: 'countryLabel', alwaysVisible: true,
        data: '/geo/europe/countries.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/europe/chokepoints_primary.geojson' },
    ],
  },

  // ============================================================
  // 合併 scene - 伊斯坦堡運河
  // 由 ArchieML 的 strait_id_override: turkish_straits_combined 切入
  // ============================================================
  turkish_straits_combined: {
    id: 'turkish_straits_combined',
    navLabel: '伊斯坦堡運河',
    title: '伊斯坦堡運河',
    // [v15] 桌機:把運河放在馬爾馬拉海完整脈絡 (西到希臘北、東到 Bolu);
    //   手機:伊斯坦堡周邊特寫,v14 微縮。桌 7%/手 26% padding
    //   (主體東西橫向 vs 直式 viewport,padding 是物理限制)
    bboxDesktop: [[25, 39], [33, 42.5]],
    bboxMobile:  [[28,40.3], [30,41.5]], 
    landSource: LAND_50M,
    layers: [
      { id: 'seas', preset: 'seaLabel', alwaysVisible: true,
        data: '/geo/europe/seas.geojson' },
      { id: 'chokepoints_primary', preset: 'chokepointPrimary', alwaysVisible: true,
        data: '/geo/europe/chokepoints_primary.geojson' },

      // 伊斯坦堡 (城市/特殊強調點,菱形)
      { id: 'emphasis_points', preset: 'emphasisPoint', alwaysVisible: true,
        data: '/geo/europe/emphasis_points.geojson' },

      // 伊斯坦堡運河 — 「規劃中的航線」用 routes_alternate 虛線
      //   alwaysVisible 因為它是 scene 主角 (跟第一島鏈的判斷一樣)
      //   GeoJSON 沒 labelLng/labelLat,featurePropertyOverrides 補
      //   fontSize 14/Mobile 13 — 比預設 12 大一級,標示敘事重點
      {
        id: 'routes_alternate', preset: 'routeAlternate', alwaysVisible: true,
        data: '/geo/europe/routes_alternate.geojson',
        featurePropertyOverrides: {
          '伊斯坦堡運河': {
            labelLng: 28.45,
            labelLat: 41.15,
            fontSize: 14,
            fontSizeMobile: 13,
            fontWeight: 500,
          },
        },
      },
    ],
  },
}

// =============================================================
// 全景地圖定義 — 八大海峽
// =============================================================
export const overviewScene = {
  id: 'overview',
  landSource: LAND_110M,
  pins: [
    { name: '荷姆茲海峽',   coord: [56.5, 26.5]  },
    { name: '曼德海峽',     coord: [43.3, 12.6]  },
    { name: '馬六甲海峽',   coord: [101.5, 2.5]  },
    { name: '麥哲倫海峽',   coord: [-70.5, -53.0] },
    { name: '台灣海峽',     coord: [120.0, 24.5], labelAnchor: 'left' },
    { name: '宮古海峽',     coord: [125.3, 24.7] },
    { name: '直布羅陀海峽', coord: [-5.4, 36.0]  },
    { name: '黑海海峽',     coord: [29.0, 41.1]  },
  ],
}

// =============================================================
// 場景閱讀順序 — 對齊 ArchieML 編輯順序 (截圖中的 1~8)
// 合併 scene 緊跟在主 scene 之後 (順序由 ArchieML 的 scroll_xxx 決定)
// =============================================================
export const sceneOrder = [
  'hormuz_strait',              // 1. 荷姆茲海峽
  'mandeb_hormuz_combined',     //    合併 scene (荷姆茲↔曼德 替代路徑)
  'bab_el_mandeb',              // 2. 曼德海峽
  'malacca_strait',             // 3. 馬六甲海峽
  'magellan',                   // 4. 麥哲倫海峽
  'taiwan_strait',              // 5. 台灣海峽
  'taiwan_strait_combined',     //    合併 scene (日本貿易航線)
  'miyako_strait',              // 6. 宮古海峽
  'gibraltar',                  // 7. 直布羅陀海峽
  'turkish_straits',            // 8. 黑海海峽
  'turkish_straits_combined',   //    合併 scene (伊斯坦堡運河)
]

// =============================================================
// navigableStraits — 給跳轉按鈕 / Tabs 使用
// 排除合併 scene (id 結尾為 _combined) — 跳轉只到主海峽
// =============================================================
export const navigableStraits = sceneOrder
  .filter(id => !id.endsWith('_combined'))
  .map(id => ({
    id,
    label: straitScenes[id].navLabel
  }))

// =============================================================
// ⭐ [v15] getSceneBbox(scene, isMobile) — 桌機/手機分版 bbox 選擇
//
//   v15 起,每個 scene 都有 bboxDesktop + bboxMobile,
//   依 isMobile (布林) 切換。bbox (舊欄位) 為 fallback,過渡期相容用。
//
//   fallback 鏈:
//     1. 對應裝置的 bbox (bboxMobile / bboxDesktop)
//     2. 另一裝置的 bbox + console.warn
//     3. 舊欄位 scene.bbox + console.warn
//     4. 全缺 → console.error + 回 null
//
//   StraitMap.svelte 用 $isMobile store 餵進來;
//   其他外部呼叫方 (StraitMapStepScroller, path_transition 等) 也用同一個 helper
// =============================================================
export function getSceneBbox(scene, isMobile) {
  if (!scene) return null
  const { id, bboxDesktop, bboxMobile, bbox } = scene
  if (isMobile) {
    if (bboxMobile) return bboxMobile
    if (bboxDesktop) {
      console.warn(`[getSceneBbox] scene "${id}" 缺 bboxMobile, fallback bboxDesktop`)
      return bboxDesktop
    }
  } else {
    if (bboxDesktop) return bboxDesktop
    if (bboxMobile) {
      console.warn(`[getSceneBbox] scene "${id}" 缺 bboxDesktop, fallback bboxMobile`)
      return bboxMobile
    }
  }
  if (bbox) {
    console.warn(`[getSceneBbox] scene "${id}" 用舊版 bbox 欄位 (應改為 bboxDesktop/bboxMobile)`)
    return bbox
  }
  console.error(`[getSceneBbox] scene "${id}" 缺所有 bbox 欄位`)
  return null
}

// =============================================================
// ⭐ 把 ArchieML 產出的 visible_layers 字串正規化成陣列
// ArchieML 輸出: "adiz|median_line"
// 我們要的:    ["adiz", "median_line"]
// =============================================================
export function normalizeVisibleLayers(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    return raw.split('|').map(s => s.trim()).filter(Boolean)
  }
  return []
}

// =============================================================
// ⭐ 從 ContentDataStore 的資料攤平成 step 陣列
// 每個 step 會帶著 { sceneId, title, body, visibleLayers,
// isSceneStart, sceneStepCount, stepIndexInScene }
// =============================================================
export function buildStepsFromContent(contentData) {
  if (!contentData) return []
  const steps = []
  for (const sceneId of sceneOrder) {
    const sceneSteps = contentData[`scroll_${sceneId}`] || []
    sceneSteps.forEach((step, idx) => {
      const rawLayers = step.visible_layers ?? step.visibleLayers
      steps.push({
        id: step.id,
        title: step.title,
        body: step.body,
        visibleLayers: normalizeVisibleLayers(rawLayers),
        sceneId,
        isSceneStart: idx === 0,
        sceneStepCount: sceneSteps.length,
        stepIndexInScene: idx,
      })
    })
  }
  return steps
}
